'use client'

import { useMemo, useState } from 'react'
import './why-hive-page.css'

/**
 * /hive/why-hive — a direct port of `public/Why Hive _ Pixelmojo.html`.
 *
 * That file is the design contract: markup, copy, and section order match it
 * one-for-one. Styling lives in the sibling `why-hive-page.css`, scoped under
 * `.why-hive-page`, with every class carrying a `wh-` prefix.
 *
 * The mock's IIFE becomes React state — the option selector and the cost model
 * are the same maths, driven by `useState`/`useMemo` instead of innerHTML.
 *
 * Deliberate breaks from the source:
 *   1. Its skip link, site header, nav, mobile menu, scroll-progress bar, and
 *      footer are dropped — the site layout already provides them.
 *   2. The hero CTAs pointed at `file:///Users/…/why-hive-redesign.html#compare`,
 *      the author's local path. They are in-page anchors here.
 *   3. The hero's `claim-note` said the 95% / 88% figures were "retained from
 *      the live page" with no linked studies. Both are now attributed inline:
 *      95% is MIT NANDA (no measurable P&L impact — NOT "fails to reach
 *      production", which is the 88% IDC figure the mock had conflated it with).
 */

type OptionId = 'hive' | 'enterprise' | 'smb' | 'agency' | 'open'

const OPTIONS: Record<
  OptionId,
  {
    name: string
    className: string
    examples: string
    price: string
    time: string
    control: string
    own: string
    color: string
    advantages: string[]
    limits: string[]
    summary: string
  }
> = {
  hive: {
    name: 'Hive',
    className: 'Ownership + managed intelligence',
    examples: 'Pixelmojo · initial build includes 2–4 coordinated agents',
    price: '$65K + $3K/mo + usage',
    time: '12 weeks',
    control: 'High',
    own: 'Application code',
    color: 'var(--w-pink)',
    advantages: [
      'Agency-grade customization',
      'Client-controlled application and data',
      'Fixed 12-week delivery',
    ],
    limits: [
      '$65K upfront build',
      '12-month platform minimum',
      'Proprietary intelligence remains operated',
    ],
    summary: 'Agency-grade customization at mid-market pricing.',
  },
  enterprise: {
    name: 'Enterprise SaaS',
    className: 'Rental',
    examples: 'Vellum · Vertex AI · Microsoft Copilot Studio',
    price: '$5K–20K/month',
    time: '2–4 weeks',
    control: 'Limited',
    own: 'No',
    color: 'var(--w-blue)',
    advantages: [
      'Fast deployment',
      'Managed infrastructure',
      'Vendor accountability',
    ],
    limits: [
      'Vendor lock-in',
      'Recurring costs compound',
      'Data-sovereignty concerns',
    ],
    summary: 'Fast and managed, with recurring rental economics.',
  },
  smb: {
    name: 'SMB SaaS',
    className: 'Rental',
    examples: 'Tidio · Zapier Agents · Persana AI',
    price: '$50–500/month',
    time: 'Days',
    control: 'Minimal',
    own: 'No',
    color: 'var(--w-cyan)',
    advantages: [
      'Low upfront cost',
      'No technical expertise needed',
      'Quick start',
    ],
    limits: [
      'Shallow integrations',
      'Per-conversation pricing',
      'Limited capabilities',
    ],
    summary: 'The lowest-risk way to validate a simple use case.',
  },
  agency: {
    name: 'Custom Agency',
    className: 'Ownership',
    examples: 'Space-O AI · N-iX · Markovate',
    price: '$100K–250K+',
    time: '6–18 months',
    control: 'Full',
    own: 'Yes',
    color: 'var(--w-yellow)',
    advantages: [
      'Complete customization',
      'Strategic differentiation',
      'Full ownership',
    ],
    limits: ['High upfront cost', 'Long timelines', 'Talent-retention risk'],
    summary: 'Maximum scope and specialization at a higher cost and timeline.',
  },
  open: {
    name: 'Open Source',
    className: 'Ownership',
    examples: 'LangChain · AutoGen · CrewAI',
    price: '$0 + infrastructure',
    time: '6–18 months',
    control: 'Full',
    own: 'Yes',
    color: 'var(--w-purple)',
    advantages: [
      'No licensing fees',
      'Cutting-edge features',
      'Active community',
    ],
    limits: [
      'Heavy engineering lift',
      'Maintenance burden',
      'No vendor accountability',
    ],
    summary:
      'Maximum technical control when the internal team can own the lift.',
  },
}

const TAB_ORDER: OptionId[] = ['enterprise', 'smb', 'agency', 'open', 'hive']

const FIELD_NODES: Array<{
  id: OptionId
  x: string
  y: string
  node: string
  label: string
  price: string
}> = [
  {
    id: 'smb',
    x: '24%',
    y: '22%',
    node: 'var(--w-cyan)',
    label: 'SMB SaaS',
    price: '$50–500/mo',
  },
  {
    id: 'enterprise',
    x: '40%',
    y: '42%',
    node: 'var(--w-blue)',
    label: 'Enterprise SaaS',
    price: '$5K–20K/mo',
  },
  {
    id: 'open',
    x: '53%',
    y: '63%',
    node: 'var(--w-purple)',
    label: 'Open source',
    price: '$0 + infra',
  },
  {
    id: 'hive',
    x: '63%',
    y: '52%',
    node: 'var(--w-pink)',
    label: 'Hive',
    price: '$65K + platform',
  },
  {
    id: 'agency',
    x: '80%',
    y: '69%',
    node: 'var(--w-yellow)',
    label: 'Custom agency',
    price: '$100K–250K+',
  },
]

const COST_NAMES = {
  hive: 'Hive',
  mid: 'Mid-tier SaaS',
  enterprise: 'Enterprise SaaS',
  agency: 'Custom agency',
} as const
type CostKey = keyof typeof COST_NAMES

/**
 * Chart series colours live in the stylesheet, not here: SVG presentation
 * attributes (`stroke=`, `fill=`) do not resolve `var()`, so a token can only
 * reach them through a class. Same approach as the hive hero relay.
 */
const SERIES_CLASS: Record<CostKey, string> = {
  hive: 'wh-series-hive',
  mid: 'wh-series-mid',
  enterprise: 'wh-series-enterprise',
  agency: 'wh-series-agency',
}

/**
 * Locale is pinned. This component is server-rendered before it hydrates, so a
 * bare toLocaleString() would format with the server's default locale in the
 * initial HTML and the visitor's in the browser, producing a hydration
 * mismatch on every number in the cost model.
 */
const money = (n: number) =>
  n >= 1e6
    ? `$${(n / 1e6).toFixed(2)}M`
    : n >= 1000
      ? `$${(n / 1000).toFixed(1)}K`
      : `$${Math.round(n).toLocaleString('en-US')}`
const full = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`

const FIT_CARDS = [
  [
    '$10M–100M revenue',
    'Mid-market company with an established technical team.',
  ],
  [
    '3+ AI touchpoints',
    'Sales, support, and operations already create context gaps.',
  ],
  [
    '50+ daily interactions',
    'Enough volume for orchestration to matter operationally.',
  ],
  [
    'Data sovereignty',
    'Regulatory or IP requirements favor controlled infrastructure.',
  ],
  [
    '2–3 year horizon',
    'Upfront build cost can be evaluated over a longer operating window.',
  ],
  [
    'Aligned incentives',
    'You accept defined resolution billing alongside fixed platform cost.',
  ],
] as const

const ALTERNATIVES = [
  [
    'Enterprise SaaS · fastest managed start',
    [
      'Limited or no technical team capacity',
      '2–4 week speed-to-value is critical',
      'Managed infrastructure and vendor accountability lead',
      'Ongoing $5K–20K/month tolerance',
    ],
  ],
  [
    'SMB SaaS · validate cheaply',
    [
      'Straightforward, single-function chatbot',
      'Predictable low volume under 1,000 conversations',
      'Testing ROI before custom investment',
      'No team to maintain a codebase',
    ],
  ],
  [
    'Custom agency · maximum specialization',
    [
      '$150K+ budget and 6–12 month tolerance',
      'Highly complex strategic differentiation',
      'Specialized computer vision or advanced NLP',
      'Ongoing co-development is desired',
    ],
  ],
  [
    'Open source · engineering-led control',
    [
      'Strong internal engineering and AI/ML expertise',
      'Bleeding-edge capabilities are essential',
      'Licensing budget is constrained',
      'Your team accepts maintenance and accountability',
    ],
  ],
] as const

const DECISION_FACTS = [
  ['Build', '$65,000 · 12 weeks'],
  ['Platform', '$3,000/month'],
  ['Usage', '$2/resolution'],
  ['Commitment', '12-month minimum'],
  ['Annual prepay', '15% discount'],
  ['Ownership', 'You own application code'],
] as const

const COMMERCIAL_CELLS = [
  ['$65K', 'One-time build · 12 weeks'],
  ['$3K/mo', 'Platform · 12-month minimum'],
  ['$2', 'Per successful resolution'],
  ['15% off', 'Annual platform prepay'],
] as const

export default function WhyHivePageContent() {
  const [selected, setSelected] = useState<OptionId>('hive')
  const [interactions, setInteractions] = useState(3000)
  const [rate, setRate] = useState(30)
  const [months, setMonths] = useState(36)
  const [midRate, setMidRate] = useState(6500)
  const [prepay, setPrepay] = useState(true)

  const option = OPTIONS[selected]

  const model = useMemo(() => {
    const resolutions = Math.round((interactions * rate) / 100)
    const platform = prepay ? 2550 : 3000
    const usageMonthly = resolutions * 2

    const costs: Record<CostKey, (m: number) => number> = {
      hive: m => 65000 + (platform + usageMonthly) * m,
      mid: m => midRate * m,
      enterprise: m => 12000 * m,
      agency: m => 150000 + 3000 * m,
    }

    const keys = Object.keys(costs) as CostKey[]
    const total = keys.reduce(
      (acc, k) => ({ ...acc, [k]: costs[k](months) }),
      {} as Record<CostKey, number>
    )
    const lowest = keys
      .map(k => [k, total[k]] as const)
      .sort((a, b) => a[1] - b[1])[0]

    const recurringGap = midRate - (platform + usageMonthly)
    const parity = recurringGap > 0 ? Math.ceil(65000 / recurringGap) : Infinity

    const yearCost = (k: CostKey, y: number) => {
      const end = Math.min(y * 12, months)
      const start = Math.min((y - 1) * 12, months)
      return end > start ? costs[k](end) - (start ? costs[k](start) : 0) : 0
    }

    // chart geometry, identical to the mock's drawChart()
    const w = 740
    const h = 270
    const pad = { l: 54, r: 18, t: 13, b: 31 }
    const max = Math.max(...keys.map(k => costs[k](months))) * 1.08
    const x = (m: number) => pad.l + (m / months) * (w - pad.l - pad.r)
    const y = (v: number) => h - pad.b - (v / max) * (h - pad.t - pad.b)

    const gridY = Array.from({ length: 5 }, (_, i) => {
      const val = (max * i) / 4
      return { val, y: y(val) }
    })
    const gridX = Array.from({ length: 4 }, (_, i) => {
      const m = Math.round((months * i) / 3)
      return { m, x: x(m) }
    })
    const lines = keys.map(k => ({
      key: k,
      d: Array.from(
        { length: months + 1 },
        (_, m) =>
          `${m ? 'L' : 'M'}${x(m).toFixed(1)},${y(costs[k](m)).toFixed(1)}`
      ).join(' '),
      cx: x(months),
      cy: y(costs[k](months)),
    }))

    return {
      resolutions,
      usageMonthly,
      total,
      lowestKey: lowest[0],
      lowestTotal: lowest[1],
      parity,
      yearCost,
      keys,
      gridY,
      gridX,
      lines,
      pad,
      w,
      h,
    }
  }, [interactions, rate, months, midRate, prepay])

  const chartLabel = `${months}-month illustrative cumulative cost chart. Hive total ${full(model.total.hive)}, mid-tier SaaS ${full(model.total.mid)}, enterprise SaaS ${full(model.total.enterprise)}, custom agency ${full(model.total.agency)}.`

  return (
    <div className='why-hive-page'>
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className='wh-hero'>
        <div className='wh-wrap wh-hero-grid'>
          <div className='wh-hero-copy'>
            <div className='wh-eyebrow-row'>
              <p className='wh-label'>The AI implementation crisis</p>
              <span className='wh-pilot'>Decision guide · 2026</span>
            </div>
            <h1 className='wh-display'>
              <em>95%</em> of AI pilots deliver no measurable return.
            </h1>
            <p className='wh-hero-lead'>
              88% of AI proof-of-concepts never reach wide-scale deployment. The
              gap between “cool demo” and “production system” is where most AI
              initiatives die.
            </p>
            <div className='wh-hero-proof'>
              <strong>→</strong>
              <p>
                <b>Hive exists to bridge that gap.</b>
                <br />
                Fixed scope. Fixed timeline. Full ownership.
              </p>
            </div>
            <div className='wh-hero-actions'>
              <a className='wh-btn wh-btn-yellow' href='#compare'>
                Compare the paths <span aria-hidden='true'>↓</span>
              </a>
              <a className='wh-btn' href='#tco'>
                Run the cost model <span aria-hidden='true'>↗</span>
              </a>
            </div>
            <p className='wh-claim-note'>
              Sources:{' '}
              <a
                href='https://mlq.ai/media/quarterly_decks/v0.1_State_of_AI_in_Business_2025_Report.pdf'
                target='_blank'
                rel='noopener noreferrer'
              >
                MIT NANDA, The GenAI Divide: State of AI in Business 2025
              </a>{' '}
              — 95% of enterprise GenAI pilots show no measurable P&amp;L
              impact.{' '}
              <a
                href='https://www.cio.com/article/3850763/88-of-ai-pilots-fail-to-reach-production-but-thats-not-all-on-it.html'
                target='_blank'
                rel='noopener noreferrer'
              >
                IDC with Lenovo, 2025, via CIO
              </a>{' '}
              — 88% of proof-of-concepts do not reach wide-scale deployment.
            </p>
          </div>

          <div className='wh-hero-visual'>
            <figure className='wh-market-field' aria-labelledby='field-caption'>
              <div className='wh-field-head'>
                <strong>Multi-agent market field</strong>
                <span>Click an option to inspect</span>
              </div>
              <div className='wh-axis-y' aria-hidden='true' />
              <div className='wh-axis-x' aria-hidden='true' />
              <div className='wh-field-hive-ring' aria-hidden='true' />
              <div className='wh-field-selection'>
                <div>
                  <span>Selected path</span>
                  <strong>{option.name}</strong>
                </div>
                <b>{option.price}</b>
              </div>
              {FIELD_NODES.map(n => (
                <button
                  key={n.id}
                  className='wh-field-node'
                  type='button'
                  aria-pressed={n.id === selected}
                  onClick={() => setSelected(n.id)}
                  style={
                    {
                      '--w-x': n.x,
                      '--w-y': n.y,
                      '--w-node': n.node,
                    } as React.CSSProperties
                  }
                >
                  <strong>{n.label}</strong>
                  <span>{n.price}</span>
                </button>
              ))}
              <figcaption className='wh-field-foot' id='field-caption'>
                <span>Source-page comparison · indicative bands</span>
                <span>Verify current vendor quotes</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ── Commercial strip ─────────────────────────────────────────── */}
      <section className='wh-commercial' aria-label='Hive commercial facts'>
        <div className='wh-wrap wh-commercial-grid'>
          <div className='wh-commercial-intro'>
            <p className='wh-label'>Hive commercial model</p>
            <p>
              Fixed build, required platform, usage tied to a defined
              resolution.
            </p>
          </div>
          {COMMERCIAL_CELLS.map(([amount, note]) => (
            <div className='wh-commercial-cell' key={amount}>
              <strong>{amount}</strong>
              <span>{note}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Compare ──────────────────────────────────────────────────── */}
      <section className='wh-section' id='compare'>
        <div className='wh-wrap'>
          <div className='wh-section-head'>
            <div>
              <p className='wh-label'>Your options for multi-agent AI</p>
              <h2 className='wh-display'>
                Every path trades one constraint for another.
              </h2>
            </div>
            <div>
              <p className='wh-section-copy'>
                The market fragments into four categories. Hive occupies the gap
                between subscription speed and fully custom ownership.
              </p>
              <p className='wh-micro'>
                Prices and examples below are retained from the live page and
                may change. Verify scope and quotes before procurement.
              </p>
            </div>
          </div>
          <div className='wh-option-shell'>
            <div className='wh-option-tabs'>
              {TAB_ORDER.map(id => (
                <button
                  key={id}
                  className='wh-option-tab'
                  type='button'
                  aria-pressed={id === selected}
                  onClick={() => setSelected(id)}
                  style={
                    {
                      '--w-tab-color': OPTIONS[id].color,
                    } as React.CSSProperties
                  }
                >
                  {OPTIONS[id].name}
                </button>
              ))}
            </div>
            <div className='wh-option-main'>
              <div className='wh-option-block'>
                <span className='wh-option-class'>{option.className}</span>
                <h3 className='wh-option-name'>{option.name}</h3>
                <p className='wh-option-examples'>{option.examples}</p>
              </div>
              <div className='wh-option-block wh-option-metrics'>
                <div className='wh-metric'>
                  <span>Price</span>
                  <strong>{option.price}</strong>
                </div>
                <div className='wh-metric'>
                  <span>Timeline</span>
                  <strong>{option.time}</strong>
                </div>
                <div className='wh-metric'>
                  <span>Customization</span>
                  <strong>{option.control}</strong>
                </div>
                <div className='wh-metric'>
                  <span>Ownership</span>
                  <strong>{option.own}</strong>
                </div>
              </div>
              <div className='wh-option-block'>
                <p className='wh-list-title'>Advantages</p>
                <ul className='wh-option-list'>
                  {option.advantages.map(a => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
                <p className='wh-list-title'>Trade-offs</p>
                <ul className='wh-option-list wh-limit'>
                  {option.limits.map(l => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className='wh-option-foot'>
              <span>{option.summary}</span>
              <span>Comparison aid · not procurement advice</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Cost model ───────────────────────────────────────────────── */}
      <section className='wh-section' id='tco'>
        <div className='wh-wrap'>
          <div className='wh-section-head'>
            <div>
              <p className='wh-label'>Decision ledger</p>
              <h2 className='wh-display'>
                Run the whole cost—not just the headline.
              </h2>
            </div>
            <div>
              <p className='wh-section-copy'>
                The live chart excludes Hive’s $2 resolution charge because
                usage varies. This model puts it back in, so the comparison
                changes with your workload.
              </p>
              <p className='wh-micro'>
                Synthetic workload · published price assumptions · excludes
                taxes, internal labor, migration, and vendor-specific overages
              </p>
            </div>
          </div>
          <div className='wh-tco'>
            <aside className='wh-controls'>
              <div className='wh-tco-head'>
                <strong>Model inputs</strong>
                <span className='wh-demo-flag'>Illustrative</span>
              </div>
              <div className='wh-control'>
                <label htmlFor='interactions'>
                  <span>Monthly interactions</span>
                  <output>{interactions.toLocaleString('en-US')}</output>
                </label>
                <input
                  className='wh-range'
                  id='interactions'
                  type='range'
                  min='0'
                  max='10000'
                  step='100'
                  value={interactions}
                  onChange={e => setInteractions(Number(e.target.value))}
                />
              </div>
              <div className='wh-control'>
                <label htmlFor='resolution-rate'>
                  <span>Billable-resolution rate</span>
                  <output>{rate}%</output>
                </label>
                <input
                  className='wh-range'
                  id='resolution-rate'
                  type='range'
                  min='0'
                  max='100'
                  step='5'
                  value={rate}
                  onChange={e => setRate(Number(e.target.value))}
                />
              </div>
              <div className='wh-control'>
                <label htmlFor='horizon'>
                  <span>Decision horizon</span>
                  <output>{months} months</output>
                </label>
                <input
                  className='wh-range'
                  id='horizon'
                  type='range'
                  min='12'
                  max='36'
                  step='12'
                  value={months}
                  onChange={e => setMonths(Number(e.target.value))}
                />
              </div>
              <div className='wh-control'>
                <label htmlFor='mid-rate'>
                  <span>Mid-tier SaaS / month</span>
                  <output>{full(midRate)}</output>
                </label>
                <input
                  className='wh-range'
                  id='mid-rate'
                  type='range'
                  min='1000'
                  max='20000'
                  step='500'
                  value={midRate}
                  onChange={e => setMidRate(Number(e.target.value))}
                />
              </div>
              <div className='wh-control'>
                <label className='wh-check'>
                  <input
                    id='prepay'
                    type='checkbox'
                    checked={prepay}
                    onChange={e => setPrepay(e.target.checked)}
                  />
                  <span>Apply 15% annual platform prepay</span>
                </label>
              </div>
              <div className='wh-assumption'>
                <strong>
                  {model.resolutions.toLocaleString('en-US')} resolutions/mo ·{' '}
                  {full(model.usageMonthly)} usage/mo
                </strong>
                <br />
                “Successful resolution” must be contractually defined before
                launch.
              </div>
            </aside>

            <div className='wh-chart-side'>
              <div className='wh-summary-row'>
                <div className='wh-summary-cell'>
                  <span>Hive modeled total</span>
                  <strong>{money(model.total.hive)}</strong>
                  <small>build + platform + usage</small>
                </div>
                <div className='wh-summary-cell'>
                  <span>Lowest modeled path</span>
                  <strong>{COST_NAMES[model.lowestKey]}</strong>
                  <small>{money(model.lowestTotal)} total</small>
                </div>
                <div className='wh-summary-cell'>
                  <span>Parity vs mid-tier</span>
                  <strong>
                    {Number.isFinite(model.parity)
                      ? model.parity <= months
                        ? `${model.parity} mo`
                        : `>${months} mo`
                      : 'None'}
                  </strong>
                  <small>cumulative model</small>
                </div>
                <div className='wh-summary-cell'>
                  <span>Usage portion</span>
                  <strong>{money(model.usageMonthly * months)}</strong>
                  <small>within Hive total</small>
                </div>
              </div>

              <div className='wh-chart-wrap'>
                <div className='wh-chart-legend'>
                  {(
                    [
                      ['var(--w-pink)', 'Hive'],
                      ['var(--w-cyan)', 'Mid-tier SaaS'],
                      ['var(--w-purple)', 'Enterprise SaaS'],
                      ['var(--w-yellow)', 'Custom agency'],
                    ] as const
                  ).map(([c, label]) => (
                    <span className='wh-legend-item' key={label}>
                      <i style={{ '--w-c': c } as React.CSSProperties} />
                      {label}
                    </span>
                  ))}
                </div>
                <svg
                  className='wh-tco-chart'
                  viewBox={`0 0 ${model.w} ${model.h}`}
                  role='img'
                  aria-label={chartLabel}
                >
                  <g>
                    {model.gridY.map(g => (
                      <g key={`y${g.y}`}>
                        <line
                          className='wh-grid-line'
                          x1={model.pad.l}
                          y1={g.y}
                          x2={model.w - model.pad.r}
                          y2={g.y}
                        />
                        <text className='wh-axis-label' x='4' y={g.y + 3}>
                          {money(g.val)}
                        </text>
                      </g>
                    ))}
                    {model.gridX.map(g => (
                      <g key={`x${g.m}`}>
                        <line
                          className='wh-grid-line'
                          x1={g.x}
                          y1={model.pad.t}
                          x2={g.x}
                          y2={model.h - model.pad.b}
                        />
                        <text
                          className='wh-axis-label'
                          x={g.x - 10}
                          y={model.h - 9}
                        >
                          {g.m}m
                        </text>
                      </g>
                    ))}
                  </g>
                  <g>
                    {model.lines.map(l => (
                      <g key={l.key}>
                        <path
                          className={`wh-cost-line ${SERIES_CLASS[l.key]}`}
                          d={l.d}
                        />
                        <circle
                          className={`wh-cost-dot ${SERIES_CLASS[l.key]}`}
                          cx={l.cx}
                          cy={l.cy}
                          r='4'
                        />
                      </g>
                    ))}
                  </g>
                </svg>
              </div>

              <div className='wh-table-wrap'>
                <table className='wh-cost-table'>
                  <thead>
                    <tr>
                      <th>Option</th>
                      <th>Year 1</th>
                      <th>Year 2</th>
                      <th>Year 3</th>
                      <th>Modeled total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {model.keys.map(k => (
                      <tr key={k} data-lowest={k === model.lowestKey}>
                        <td>
                          {COST_NAMES[k]}
                          {k === model.lowestKey && (
                            <span className='wh-low-badge'>Lowest modeled</span>
                          )}
                        </td>
                        <td>{money(model.yearCost(k, 1))}</td>
                        <td>
                          {months > 12 ? money(model.yearCost(k, 2)) : '—'}
                        </td>
                        <td>
                          {months > 24 ? money(model.yearCost(k, 3)) : '—'}
                        </td>
                        <td>
                          <strong>{money(model.total[k])}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className='wh-tco-note'>
                Published page benchmark: Hive fixed cost uses a $65K build and
                either $3K/month or $30,600/year with annual prepay. Competitor
                assumptions: mid-tier SaaS $6.5K/month, enterprise SaaS
                $12K/month, custom agency $150K build + $3K/month maintenance.
              </div>
              <div className='wh-sr-status' role='status' aria-live='polite'>
                Illustrative {months}-month model updated. Hive{' '}
                {full(model.total.hive)} including{' '}
                {model.resolutions.toLocaleString('en-US')} resolutions per
                month. Lowest modeled path {COST_NAMES[model.lowestKey]} at{' '}
                {full(model.lowestTotal)}.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Fit ──────────────────────────────────────────────────────── */}
      <section className='wh-section' id='fit'>
        <div className='wh-wrap'>
          <div className='wh-section-head'>
            <div>
              <p className='wh-label'>Choose the right-sized answer</p>
              <h2 className='wh-display'>Hive is specific—not universal.</h2>
            </div>
            <p className='wh-section-copy'>
              The strongest buying signal is coordination pain across multiple
              AI touchpoints. If that is not your problem yet, another path may
              be better.
            </p>
          </div>
          <div className='wh-fit-board'>
            <article className='wh-fit-side'>
              <p className='wh-label'>When Hive makes strategic sense</p>
              <h3>You have enough complexity to coordinate.</h3>
              <div className='wh-fit-grid'>
                {FIT_CARDS.map(([title, copy]) => (
                  <div className='wh-fit-card' key={title}>
                    <strong>{title}</strong>
                    <span>{copy}</span>
                  </div>
                ))}
              </div>
            </article>
            <article className='wh-fit-side'>
              <p className='wh-label'>When alternatives are better</p>
              <h3>Choose the constraint that matters most.</h3>
              <div className='wh-alt-list'>
                {ALTERNATIVES.map(([summary, items]) => (
                  <details key={summary}>
                    <summary>{summary}</summary>
                    <ul>
                      {items.map(i => (
                        <li key={i}>{i}</li>
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────── */}
      <section className='wh-section'>
        <div className='wh-wrap wh-decision-strip'>
          <div className='wh-decision-copy'>
            <p className='wh-label' style={{ color: 'var(--w-white)' }}>
              Pilot program open
            </p>
            <h2 className='wh-display'>Ready to bridge the gap?</h2>
            <p>
              We&apos;re selective about pilot partners. 30-minute call to
              assess fit. If Hive isn&apos;t right for your situation,
              we&apos;ll tell you—and recommend alternatives.
            </p>
            <div className='wh-cta-row'>
              <a className='wh-btn wh-btn-yellow' href='/contact-us'>
                Request pilot access <span aria-hidden='true'>↗</span>
              </a>
              <a className='wh-btn' href='/hive'>
                Learn more about Hive <span aria-hidden='true'>→</span>
              </a>
            </div>
          </div>
          <div className='wh-decision-facts'>
            {DECISION_FACTS.map(([label, value]) => (
              <div className='wh-fact' key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
